import { useState, type ReactNode } from 'react'
import { Eye, EyeOff, MapPin, Pause, Play, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateAdAccount } from '@/hooks/useAds'
import { cn } from '@/lib/utils'
import { formatGoogleAdsId } from '@lib/helpers/google-ads-id'
import type { AdAccountDto, AdVenueOptionDto } from '@lib/dto/ads.dto'
import type { UpdateAdAccountInput } from '@lib/input/ads.input'
import { adAccountStatus } from './adAccountStatus'
import { adPlaceLabel } from './adPlaces'
import { AdPlaceDialog } from './AdPlaceDialog'
import { RemoveAdAccountDialog } from './RemoveAdAccountDialog'

interface AdAccountTableProps {
  accounts: AdAccountDto[]
  /** Connections whose sign-in Google refuses: an account reached only through them isn't updating. */
  failingConnectionIds?: ReadonlySet<string>
  /** The control at the right of the list's band: refreshing. */
  action?: ReactNode
  venues: AdVenueOptionDto[]
  /** Accounts the last sign-in found for the first time, marked so they can be found. */
  newAccountIds?: ReadonlySet<string>
  /** The venue the last sign-in started from: where its new accounts are offered. */
  suggestedVenueId?: string | null
}

/**
 * Waiting accounts first — they want a decision — then those in use, stopped and
 * ignored. A closed account that isn't in use goes last: there is nothing to
 * decide about it until Google reopens it.
 */
const ORDER: Record<AdAccountDto['use'], number> = { not_in_use: 0, in_use: 1, stopped: 2, ignored: 3 }

function rank(account: AdAccountDto): number {
  if (account.closedSince !== null && account.use === 'not_in_use') return 4
  return ORDER[account.use]
}

/** Where an account's campaigns count, in words. */
function placeLabel(account: AdAccountDto, venues: AdVenueOptionDto[]): string {
  if (account.defaultVenueId === null) return '—'
  return adPlaceLabel({ venueId: account.defaultVenueId, trackId: account.defaultTrackId }, venues) ?? 'A deleted venue'
}

/**
 * Every Google Ads account the company's connections reach, with where it counts
 * and its status, and what can be done with it from its row.
 */
export function AdAccountTable({
  accounts,
  failingConnectionIds,
  action,
  venues,
  newAccountIds,
  suggestedVenueId = null,
}: AdAccountTableProps) {
  const update = useUpdateAdAccount()
  const { toast } = useToast()
  const [placing, setPlacing] = useState<AdAccountDto | null>(null)
  const [removing, setRemoving] = useState<AdAccountDto | null>(null)
  const sorted = [...accounts].sort((a, b) => rank(a) - rank(b))

  function change(account: AdAccountDto, input: UpdateAdAccountInput) {
    update.mutate({ accountId: account.id, input }, { onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }) })
  }

  function actionsFor(account: AdAccountDto): RowAction[] {
    const closed = account.closedSince !== null
    const putInUse: RowAction = {
      label: 'Put in use…',
      icon: Plus,
      priority: true,
      onSelect: () => setPlacing(account),
      disabled: closed,
      disabledReason: closed ? 'It is closed in Google, so there is nothing to bring in.' : undefined,
    }
    const repoint: RowAction = { label: 'Change where it counts…', icon: MapPin, onSelect: () => setPlacing(account) }
    const remove: RowAction = { label: 'Remove from venue…', icon: Trash2, destructive: true, onSelect: () => setRemoving(account) }
    switch (account.use) {
      case 'not_in_use':
        return [putInUse, { label: 'Ignore', icon: EyeOff, onSelect: () => change(account, { action: 'ignore' }) }]
      case 'ignored':
        return [putInUse, { label: 'Stop ignoring', icon: Eye, onSelect: () => change(account, { action: 'unignore' }) }]
      case 'in_use':
        return [repoint, { label: 'Stop updating', icon: Pause, onSelect: () => change(account, { action: 'stop' }) }, remove]
      case 'stopped':
        return [repoint, { label: 'Start updating', icon: Play, onSelect: () => change(account, { action: 'start' }) }, remove]
    }
  }

  return (
    <>
      <Table heading={accounts.length === 1 ? '1 account' : `${String(accounts.length)} accounts`} actions={action}>
        <TableHeader>
          <TableHead>Account</TableHead>
          <TableHead>Counts toward</TableHead>
          <TableHead>Currency</TableHead>
          <TableHead>Status</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {accounts.length === 0 ? (
            <TableEmptyRow colSpan={5}>No Google Ads accounts yet. Connect Google Ads to find them.</TableEmptyRow>
          ) : null}
          {sorted.map((account) => {
            const status = adAccountStatus(account, failingConnectionIds)
            return (
              <TableRow
                key={account.id}
                selected={newAccountIds?.has(account.id) ?? false}
                className={cn(account.use === 'ignored' && 'text-ink-muted')}
              >
                <TableCell className="min-w-48">
                  <div className={cn('font-medium', account.use === 'ignored' ? 'text-ink-muted' : 'text-ink')}>{account.name}</div>
                  <div className="whitespace-nowrap text-xs text-ink-muted tabular-nums">{formatGoogleAdsId(account.externalId)}</div>
                </TableCell>
                <TableCell className="min-w-40">{placeLabel(account, venues)}</TableCell>
                <TableCell className="tabular-nums">{account.currencyCode}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Badge tone={status.tone}>{status.label}</Badge>
                </TableCell>
                <TableActionsCell>
                  <RowActions label={account.name} actions={actionsFor(account)} />
                </TableActionsCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {placing !== null ? (
        <AdPlaceDialog
          account={placing}
          venues={venues}
          suggestedVenueId={newAccountIds?.has(placing.id) === true ? suggestedVenueId : null}
          onClose={() => setPlacing(null)}
        />
      ) : null}
      {removing !== null ? <RemoveAdAccountDialog account={removing} onClose={() => setRemoving(null)} /> : null}
    </>
  )
}
